import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';

import {
  BorderColor,
  CmsImage,
  DualOverlainImage,
  InlineImage,
  OverlayDirection,
} from '@digital-platform/rs-cms/domain';
import { ViewportService } from '@digital-platform/rs-shared/services';
import { BasePTypeComponent } from '../base-ptype/base-ptype.component';
import { InlineImageComponent } from '../inline-image/inline-image.component';

@Component({
  selector: 'digital-platform-dual-overlain-image',
  standalone: true,
  imports: [CommonModule, InlineImageComponent],
  templateUrl: './dual-overlain-image.component.html',
  styleUrls: ['./dual-overlain-image.component.scss'],
})
export class DualOverlainImageComponent
  extends BasePTypeComponent
  implements DualOverlainImage
{
  public override type = 'dualOverlainImage' as const;

  public viewportService = inject(ViewportService);

  @Input() public baseImage: InlineImage[] = [];
  @Input() public overlayImage: InlineImage[] = [];

  @Input() public overlayBorderColor: BorderColor | null = {};
  @Input() public overlayBorderWidth = '2.0';
  @Input() public overlayDirection: OverlayDirection = {};
  @Input() public overlayHorizPosition = '53.00';
  @Input() public overlayVertPosition = '42.00';

  get isMobile() {
    return this.viewportService.isMobile;
  }

  get selectedOverlayImage(): CmsImage | null {
    return this.getSelectedImage(this.overlayImage?.[0]);
  }

  get selectedBaseImage(): CmsImage | null {
    return this.getSelectedImage(this.baseImage?.[0]);
  }

  get hasEnoughRoomToOffset() {
    return this.viewportService.isDesktop;
  }

  get overlayLeftPosition() {
    if (this.isMobile) return '';
    return this.overlayDirection?.overlayDir !== 'right'
      ? 'auto'
      : !this.hasEnoughRoomToOffset
      ? 0
      : `${+this.overlayHorizPosition || 0}%`;
  }

  get overlayRightPosition() {
    if (this.isMobile) return '';
    return this.overlayDirection?.overlayDir === 'right'
      ? 'auto'
      : !this.hasEnoughRoomToOffset
      ? 0
      : `${+this.overlayHorizPosition || 0}%`;
  }

  get overlayOffsetRight() {
    if (this.isMobile) return 0;
    return this.overlayDirection?.overlayDir !== 'right'
      ? 0
      : this.overlayOffset;
  }

  get overlayOffsetLeft() {
    if (this.isMobile) return 0;
    return this.overlayDirection?.overlayDir === 'right'
      ? 0
      : this.overlayOffset;
  }

  private get overlayOffset() {
    if (!this.overlayHorizPosition || !this.hasEnoughRoomToOffset) return 0;
    const baseWidth = this.selectedBaseImage?.width ?? 0;
    if (!baseWidth) return 0;
    const overlayWidth = this.selectedOverlayImage?.width ?? 0;
    if (!overlayWidth) return 0;
    const percentage = +this.overlayHorizPosition / 100;
    const xShift = percentage * baseWidth;
    return overlayWidth - xShift;
  }

  private getSelectedImage(imageScope: InlineImage) {
    if (this.viewportService.isMobile) {
      return (
        imageScope?.webpMedia ??
        imageScope?.webpMediaMid ??
        imageScope?.webpMediaDesktop
      );
    } else if (this.viewportService.isMidsize) {
      return (
        imageScope?.webpMediaMid ??
        imageScope?.webpMediaDesktop ??
        imageScope?.webpMedia
      );
    } else {
      return (
        imageScope?.webpMediaDesktop ??
        imageScope?.webpMediaMid ??
        imageScope?.webpMedia
      );
    }
  }
}
